import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Modal, Form, Input, Select, Tag, Tabs, Typography, Space, Empty, message, Popconfirm, Descriptions, Divider, Badge, Avatar, Rate, Switch } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, StarOutlined, EyeOutlined, CopyOutlined, GlobalOutlined, LockOutlined, ThunderboltOutlined, HeartOutlined, LikeOutlined } from '@ant-design/icons';
import { presetAPI, userAPI } from '../services/api';
import { t } from '../utils/i18n';
import { BASE_URL } from '../utils/apiUrl';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const Presets = () => {
  const [activeTab, setActiveTab] = useState('market');
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [form] = Form.useForm();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, pageSize: 12, total: 0 });
  const [models, setModels] = useState([]);
  const [likedPresets, setLikedPresets] = useState(new Set()); // 存储用户点赞的预设ID

  // 加载模型列表
  useEffect(() => {
    const loadModels = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/models`);
        const data = await res.json();
        if (data.data?.list) {
          setModels(data.data.list.map(m => ({
            value: m.model_name,
            label: `${m.display_name || m.model_name} (${m.provider?.display_name || ''})`,
          })));
        }
      } catch (err) {
        console.error('Failed to load models:', err);
      }
    };
    loadModels();
  }, []);

  // 加载预设
  const loadPresets = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.pageSize,
      };
      if (searchKeyword) params.keyword = searchKeyword;
      if (selectedCategory !== 'all') params.category = selectedCategory;

      let res;
      if (activeTab === 'market') {
        res = await presetAPI.getPublicPresets(params);
        // 市场页面加载点赞状态
        loadLikeStatus(res.data?.items || []);
      } else if (activeTab === 'liked') {
        res = await presetAPI.getMyLikedPresets(params);
        // 点赞列表中的预设都是已点赞的
        const likedSet = new Set((res.data?.items || []).map(p => p.id));
        setLikedPresets(likedSet);
      } else {
        res = await presetAPI.getMyPresets(params);
      }
      setPresets(res.data?.items || []);
      setPagination(p => ({ ...p, total: res.data?.total || 0 }));
    } catch (err) {
      message.error(t('load_failed'));
    } finally {
      setLoading(false);
    }
  };

  // 加载点赞状态
  const loadLikeStatus = async (presetsList) => {
    const newLikedSet = new Set();
    for (const preset of presetsList) {
      try {
        const res = await presetAPI.getLikeStatus(preset.id);
        if (res.data?.is_liked) {
          newLikedSet.add(preset.id);
        }
      } catch (err) {
        // 忽略错误
      }
    }
    setLikedPresets(prev => {
      const merged = new Set(prev);
      newLikedSet.forEach(id => merged.add(id));
      return merged;
    });
  };

  // 点赞/取消点赞
  const handleLike = async (preset, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await presetAPI.toggleLike(preset.id);
      const newLikedSet = new Set(likedPresets);
      if (res.data?.is_liked) {
        newLikedSet.add(preset.id);
        message.success(t('like_success'));
      } else {
        newLikedSet.delete(preset.id);
        message.success(t('unlike_success'));
      }
      setLikedPresets(newLikedSet);
      // 更新预设列表中的点赞数
      setPresets(presets.map(p => 
        p.id === preset.id 
          ? { ...p, like_count: res.data?.like_count ?? p.like_count }
          : p
      ));
      // 更新当前选中的预设（用于详情弹窗）
      if (selectedPreset && selectedPreset.id === preset.id) {
        setSelectedPreset(prev => ({ 
          ...prev, 
          like_count: res.data?.like_count ?? prev.like_count 
        }));
      }
    } catch (err) {
      message.error(t('operation_failed'));
    }
  };

  useEffect(() => {
    loadPresets();
  }, [activeTab, pagination.page, searchKeyword, selectedCategory]);

  // 创建预设
  const handleCreate = async (values) => {
    try {
      await presetAPI.createPreset(values);
      message.success(t('create_success'));
      setCreateModalVisible(false);
      form.resetFields();
      loadPresets();
    } catch (err) {
      message.error(t('create_failed'));
    }
  };

  // 更新预设
  const handleUpdate = async (values) => {
    try {
      await presetAPI.updatePreset(selectedPreset.id, values);
      message.success(t('update_success'));
      setDetailModalVisible(false);
      loadPresets();
    } catch (err) {
      message.error(t('update_failed'));
    }
  };

  // 删除预设
  const handleDelete = async (id) => {
    try {
      await presetAPI.deletePreset(id);
      message.success(t('delete_success'));
      loadPresets();
    } catch (err) {
      message.error(t('delete_failed'));
    }
  };

  // 使用预设
  const handleUse = (preset) => {
    message.success(t('using_preset') + ': ' + preset.name);
    // 跳转到对话页面并应用预设
    window.location.href = `/dashboard?preset_id=${preset.id}`;
  };

  // 复制预设配置
  const handleCopy = async (preset) => {
    try {
      await presetAPI.copyPreset(preset.id);
      message.success(t('copy_success'));
      if (activeTab === 'my') loadPresets();
    } catch (err) {
      message.error(t('copy_failed'));
    }
  };

  // 切换公开状态
  const handleTogglePublic = async (preset) => {
    try {
      await presetAPI.updatePreset(preset.id, { is_public: !preset.is_public });
      message.success(preset.is_public ? t('make_private_success') : t('make_public_success'));
      loadPresets();
    } catch (err) {
      message.error(t('operation_failed'));
    }
  };

  const showDetail = (preset) => {
    setSelectedPreset(preset);
    setDetailModalVisible(true);
  };

  const categories = [
    { key: 'all', label: t('all_categories') },
    { key: 'coding', label: t('cat_code'), color: 'blue' },
    { key: 'writing', label: t('cat_writing'), color: 'green' },
    { key: 'analysis', label: t('cat_analysis'), color: 'purple' },
    { key: 'product', label: t('cat_product'), color: 'orange' },
    { key: 'education', label: t('cat_education'), color: 'cyan' },
    { key: 'research', label: t('cat_research'), color: 'gold' },
    { key: 'translation', label: t('cat_translation'), color: 'magenta' },
    { key: 'others', label: t('cat_others'), color: 'default' },
  ];

  const getCategoryColor = (cat) => {
    const found = categories.find(c => c.key === cat);
    return found?.color || 'default';
  };

  // 预设卡片
  const PresetCard = ({ preset }) => {
    const categoryColors = {
      coding: { bg: '#e6f7ff', icon: '#1890ff' },
      writing: { bg: '#f6ffed', icon: '#52c41a' },
      analysis: { bg: '#f9f0ff', icon: '#722ed1' },
      product: { bg: '#fff7e6', icon: '#fa8c16' },
      education: { bg: '#e6fffb', icon: '#13c2c2' },
      research: { bg: '#fff0f6', icon: '#eb2f96' },
      translation: { bg: '#f0f5ff', icon: '#597ef7' },
      others: { bg: '#f5f5f5', icon: '#8c8c8c' },
    };
    const colors = categoryColors[preset.category] || categoryColors.others;
    const isLiked = likedPresets.has(preset.id);
    
    return (
      <Card
        hoverable
        className="preset-card"
        style={{ 
          borderRadius: 12, 
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: 0 } }}
      >
        {/* 顶部渐变背景 */}
        <div style={{
          height: 80,
          background: `linear-gradient(135deg, ${colors.icon}15, ${colors.icon}05)`,
          borderBottom: `3px solid ${colors.icon}30`,
          padding: '16px 16px 12px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: colors.bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18,
            }}>
              <ThunderboltOutlined style={{ color: colors.icon }} />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: '#262626', display: 'flex', alignItems: 'center', gap: 4 }}>
                {preset.is_public ? <GlobalOutlined style={{ fontSize: 12, color: '#52c41a' }} /> : <LockOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
                {preset.name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                {preset.model && (
                  <Tag color="cyan" style={{ fontSize: 11, marginBottom: 0 }}>
                    {preset.model}
                  </Tag>
                )}
                <Tag color={getCategoryColor(preset.category)} style={{ marginBottom: 0, fontSize: 11 }}>
                  {categories.find(c => c.key === preset.category)?.label || preset.category}
                </Tag>
              </div>
            </div>
          </div>
          {/* 点赞按钮 */}
          {(activeTab === 'market' || activeTab === 'liked') && (
            <Button
              type="text"
              icon={isLiked ? <HeartOutlined style={{ color: '#ff4d4f', fontSize: 18 }} /> : <LikeOutlined style={{ fontSize: 18 }} />}
              onClick={(e) => handleLike(preset, e)}
              style={{ padding: '4px 8px', height: 'auto' }}
            />
          )}
        </div>
        
        {/* 内容区域 */}
        <div style={{ padding: '12px 16px' }}>
          <Paragraph 
            ellipsis={{ rows: 2 }} 
            style={{ marginBottom: 8, color: '#595959', fontSize: 13, minHeight: 40 }}
          >
            {preset.description || t('no_description')}
          </Paragraph>
          
          {/* 统计信息 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <StarOutlined style={{ color: '#faad14', fontSize: 12 }} />
                <span style={{ fontSize: 12, color: '#595959' }}>{preset.use_count || preset.usage_count || 0}</span>
                <span style={{ fontSize: 11, color: '#8c8c8c' }}>{t('usage')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <HeartOutlined style={{ color: isLiked ? '#ff4d4f' : '#d9d9d9', fontSize: 12 }} />
                <span style={{ fontSize: 12, color: '#595959' }}>{preset.like_count || 0}</span>
                <span style={{ fontSize: 11, color: '#8c8c8c' }}>{t('likes')}</span>
              </div>
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {dayjs(preset.created_at).fromNow()}
            </Text>
          </div>
          
          {/* 操作按钮 */}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button 
              type="primary" 
              icon={<ThunderboltOutlined />}
              onClick={() => handleUse(preset)}
              style={{ flex: 1, borderRadius: 6 }}
              size="small"
            >
              {t('use_preset')}
            </Button>
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => showDetail(preset)}
              style={{ borderRadius: 6 }}
              size="small"
            />
            <Button 
              type="text" 
              icon={<CopyOutlined />} 
              onClick={() => handleCopy(preset)}
              style={{ borderRadius: 6 }}
              size="small"
            />
            {activeTab === 'my' && (
              <>
                <Button 
                  type="text" 
                  icon={<EditOutlined />} 
                  onClick={() => { setSelectedPreset(preset); setCreateModalVisible(true); form.setFieldsValue(preset); }}
                  style={{ borderRadius: 6 }}
                  size="small"
                />
                <Popconfirm title={t('confirm_delete')} onConfirm={() => handleDelete(preset.id)}>
                  <Button type="text" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} size="small" />
                </Popconfirm>
              </>
            )}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="presets-page" style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>
          <ThunderboltOutlined /> {t('preset_management')}
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => { setSelectedPreset(null); form.resetFields(); setCreateModalVisible(true); }}>
          {t('create_preset')}
        </Button>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={(key) => { setActiveTab(key); setPagination(p => ({ ...p, page: 1 })); }}
        items={[
          {
            key: 'market',
            label: <span><GlobalOutlined /> {t('preset_market')}</span>,
          },
          {
            key: 'my',
            label: <span><LockOutlined /> {t('my_presets')}</span>,
          },
          {
            key: 'liked',
            label: <span><HeartOutlined /> {t('my_liked_presets')}</span>,
          },
        ]}
      />

      {activeTab === 'market' && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Input.Search
            placeholder={t('search_preset')}
            prefix={<SearchOutlined />}
            style={{ width: 250 }}
            allowClear
            value={searchKeyword}
            onChange={(e) => {
              setSearchKeyword(e.target.value);
            }}
            onSearch={(value) => {
              setSearchKeyword(value);
              setPagination(p => ({ ...p, page: 1 }));
            }}
          />
          <Select
            value={selectedCategory}
            onChange={setSelectedCategory}
            style={{ width: 150 }}
          >
            {categories.map(c => (
              <Select.Option key={c.key} value={c.key}>{c.label}</Select.Option>
            ))}
          </Select>
        </div>
      )}

      {presets.length > 0 ? (
        <Row gutter={[16, 16]}>
          {presets.map(preset => (
            <Col key={preset.id} xs={24} sm={12} md={8} lg={6}>
              <PresetCard preset={preset} />
            </Col>
          ))}
        </Row>
      ) : (
        <Empty description={t('no_presets')} style={{ marginTop: 100 }} />
      )}

      {/* 创建/编辑弹窗 */}
      <Modal
        title={selectedPreset ? t('edit_preset') : t('create_preset')}
        open={createModalVisible}
        onCancel={() => { setCreateModalVisible(false); setSelectedPreset(null); form.resetFields(); }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={selectedPreset ? handleUpdate : handleCreate}>
          <Form.Item name="name" label={t('preset_name')} rules={[{ required: true }]}>
            <Input placeholder={t('enter_preset_name')} />
          </Form.Item>

          <Form.Item name="description" label={t('preset_description')}>
            <TextArea rows={3} placeholder={t('enter_description')} />
          </Form.Item>

          <Form.Item name="category" label={t('category')} rules={[{ required: true }]}>
            <Select>
              {categories.filter(c => c.key !== 'all').map(c => (
                <Select.Option key={c.key} value={c.key}>{c.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="model" label={t('binding_model')}>
            <Select
              allowClear
              placeholder={t('binding_model_placeholder')}
              showSearch
              optionFilterProp="children"
            >
              {models.map(m => (
                <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="prompt" label={t('preset_prompt')} rules={[{ required: true }]}>
            <TextArea rows={6} placeholder={t('enter_prompt')} />
          </Form.Item>

          <Form.Item name="variables" label={t('variables')}>
            <TextArea rows={2} placeholder={t('variables_placeholder')} />
          </Form.Item>

          <Form.Item name="is_public" label={t('visibility')} valuePropName="checked" initialValue={false}>
            <Switch checkedChildren={<GlobalOutlined />} unCheckedChildren={<LockOutlined />} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>
                {t('cancel')}
              </Button>
              <Button type="primary" htmlType="submit">
                {selectedPreset ? t('save') : t('create')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情弹窗 */}
      <Modal
        title={selectedPreset?.name}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={
          <Space>
            {(activeTab === 'market' || activeTab === 'liked') && (
              <Button 
                icon={likedPresets.has(selectedPreset?.id) ? <HeartOutlined style={{ color: '#ff4d4f' }} /> : <LikeOutlined />} 
                onClick={(e) => handleLike(selectedPreset, e)}
              >
                {likedPresets.has(selectedPreset?.id) ? t('unlike') : t('like')}
              </Button>
            )}
            <Button onClick={() => setDetailModalVisible(false)}>{t('close')}</Button>
            <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => { handleUse(selectedPreset); setDetailModalVisible(false); }}>
              {t('use_preset')}
            </Button>
          </Space>
        }
        width={700}
      >
        {selectedPreset && (
          <>
            <Descriptions column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label={t('category')}>
                <Tag color={getCategoryColor(selectedPreset.category)}>
                  {categories.find(c => c.key === selectedPreset.category)?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('visibility')}>
                {selectedPreset.is_public ? <Tag icon={<GlobalOutlined />} color="blue">{t('public')}</Tag> : <Tag icon={<LockOutlined />}>{t('private')}</Tag>}
              </Descriptions.Item>
              {selectedPreset.model && (
                <Descriptions.Item label={t('binding_model')}>
                  <Tag color="cyan">{selectedPreset.model}</Tag>
                </Descriptions.Item>
              )}
              <Descriptions.Item label={t('usage_count')}>
                <Space><StarOutlined style={{ color: '#faad14' }} />{selectedPreset.use_count || selectedPreset.usage_count || 0} {t('usage')}</Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('like_count')}>
                <Space><HeartOutlined style={{ color: likedPresets.has(selectedPreset.id) ? '#ff4d4f' : '#d9d9d9' }} />{selectedPreset.like_count || 0} {t('likes')}</Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('created_at')}>
                {dayjs(selectedPreset.created_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
            </Descriptions>
            <Divider>{t('description')}</Divider>
            <Paragraph>{selectedPreset.description || t('no_description')}</Paragraph>
            <Divider>{t('preset_prompt')}</Divider>
            <pre style={{
              background: '#f5f5f5',
              padding: '12px',
              borderRadius: '4px',
              maxHeight: 200,
              overflow: 'auto',
              whiteSpace: 'pre-wrap'
            }}>
              {selectedPreset.prompt}
            </pre>
            {selectedPreset.variables && (
              <>
                <Divider>{t('variables')}</Divider>
                <pre style={{ background: '#f5f5f5', padding: '12px', borderRadius: '4px' }}>
                  {selectedPreset.variables}
                </pre>
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default Presets;
